import httpClient from './httpClient';
import axios from 'axios';

// Configuration for n8n webhook
const N8N_WEBHOOK_CONFIG = {
  baseUrl: process.env.REACT_APP_N8N_WEBHOOK_URL || '/api/agent-chat',
  timeout: 600000, // 10 minutes timeout for regular messages (AI can take long)
  fileTimeout: 600000, // 10 minutes timeout for file uploads
  streamTimeout: 900000, // 15 minutes for streaming requests
  maxRetries: 3,
  retryDelay: 1000, // 1 second initial delay
};

class AgentChatService {
  constructor() {
    this.activeRequests = new Map();
    this.rateLimitReset = null;
    this.requestQueue = [];
    this.isProcessingQueue = false;
  }

  /**
   * Send a message to the n8n webhook
   * @param {Object} payload - Message payload
   * @param {string} payload.message - The message text
   * @param {File|File[]} payload.files - Optional file attachment(s)
   * @param {Object} payload.metadata - Optional metadata
   * @param {Object} options - Request options
   * @returns {Promise} Response from n8n
   */
  async sendMessage(payload, options = {}) {
    const requestId = this.generateRequestId();

    try {
      // Check rate limiting
      if (this.isRateLimited()) {
        throw new Error('请求过频。请稍候再发送消息。');
      }

      // Prepare request data
      const formData = new FormData();
      formData.append('message', payload.message || '');
      formData.append('sessionId', this.getSessionId());
      formData.append('timestamp', new Date().toISOString());

      // Handle multiple files - n8n standard format
      if (payload.files) {
        const files = Array.isArray(payload.files) ? payload.files : [payload.files];
        console.log('📁 Sending files to n8n:', files.map(f => ({ name: f.name, size: f.size, type: f.type })));

        // n8n expects files with 'files' field name
        files.forEach((file, index) => {
          formData.append('files', file);  // Standard n8n format
          formData.append(`file_${index}`, file);  // Backup format
          formData.append(`fileName_${index}`, file.name);
          formData.append(`fileType_${index}`, file.type);
          formData.append(`fileSize_${index}`, file.size);
        });
        formData.append('fileCount', files.length);
        console.log('📤 FormData prepared with', files.length, 'files in both formats');
      }

      // Backward compatibility for single file
      if (payload.file && !payload.files) {
        formData.append('file', payload.file);
        formData.append('fileName', payload.file.name);
        formData.append('fileType', payload.file.type);
        formData.append('fileSize', payload.file.size);
      }

      if (payload.metadata) {
        formData.append('metadata', JSON.stringify(payload.metadata));
      }

      // Add to active requests
      const controller = new AbortController();
      this.activeRequests.set(requestId, controller);

      // Send request with retry logic
      const response = await this.sendWithRetry(
        formData,
        {
          ...options,
          signal: controller.signal,
          requestId,
          // Override Content-Type for FormData to let browser set multipart/form-data
          isFormData: true,
        }
      );

      // Remove from active requests
      this.activeRequests.delete(requestId);

      return this.processResponse(response);
    } catch (error) {
      this.activeRequests.delete(requestId);
      throw this.handleError(error);
    }
  }

  /**
   * Send message with retry logic
   */
  async sendWithRetry(formData, options, retryCount = 0) {
    try {
      // Use longer timeout for file uploads to prevent premature timeouts
      const timeoutToUse = options.isFormData ? N8N_WEBHOOK_CONFIG.fileTimeout : N8N_WEBHOOK_CONFIG.timeout;

      // Prepare base configuration
      const config = {
        timeout: timeoutToUse,
        signal: options.signal,
        onUploadProgress: options.onUploadProgress,
      };

      // Prepare headers - for FormData, don't set Content-Type
      if (options.isFormData) {
        // For FormData, let browser set Content-Type with boundary
        config.headers = {
          'X-Request-ID': options.requestId,
          'X-Session-ID': this.getSessionId(),
          // Don't set Content-Type - browser will set multipart/form-data
        };
        // Create a fresh axios instance without the default Content-Type
        const formDataClient = axios.create({
          baseURL: httpClient.defaults.baseURL,
        });
        // Copy authorization header from httpClient
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        const response = await formDataClient.post(
          N8N_WEBHOOK_CONFIG.baseUrl,
          formData,
          config
        );
        return response;
      } else {
        // Regular JSON request
        config.headers = {
          'X-Request-ID': options.requestId,
          'X-Session-ID': this.getSessionId(),
        };

        const response = await httpClient.post(
          N8N_WEBHOOK_CONFIG.baseUrl,
          formData,
          config
        );
        return response;
      }
    } catch (error) {
      // Check if we should retry (but never retry file uploads to prevent duplicate n8n executions)
      if (!options.isFormData && this.shouldRetry(error, retryCount)) {
        const delay = this.calculateRetryDelay(retryCount);
        await this.delay(delay);
        return this.sendWithRetry(formData, options, retryCount + 1);
      }

      throw error;
    }
  }

  /**
   * Stream response from n8n webhook with file support using true SSE
   */
  async streamMessage(payload, onChunk, options = {}) {
    const requestId = this.generateRequestId();

    try {
      // Check rate limiting
      if (this.isRateLimited()) {
        throw new Error('请求过频。请稍候再发送消息。');
      }

      // For streaming with files, we need to send everything together to the Flask backend
      // The Flask backend will handle both file upload and streaming to n8n
      if (payload.files || payload.file) {
        // Send files + message via POST and get streaming response
        return await this.streamWithFiles(payload, onChunk, requestId);
      }

      // For text-only messages, use SSE endpoint
      const sessionId = this.getSessionId();
      const sseParams = new URLSearchParams({
        sessionId: sessionId,
        requestId: requestId,
        message: payload.message || '',
        timestamp: new Date().toISOString(),
        stream: 'true'
      });

      if (payload.metadata) {
        sseParams.append('metadata', JSON.stringify(payload.metadata));
      }

      const sseUrl = `${httpClient.defaults.baseURL}${N8N_WEBHOOK_CONFIG.baseUrl}/stream?${sseParams.toString()}`;
      console.log('🔗 Establishing SSE connection for text-only:', sseUrl);

      const eventSource = new EventSource(sseUrl);

      // Track active connection
      this.activeRequests.set(requestId, eventSource);

      return new Promise((resolve, reject) => {
        let fullResponse = '';

        eventSource.onopen = () => {
          console.log('✅ SSE connection established');
        };

        eventSource.onmessage = (event) => {
          try {
            console.log('📨 SSE raw data:', event.data);
            const data = JSON.parse(event.data);
            console.log('📨 SSE parsed:', data);

            if (data.type === 'content') {
              fullResponse += data.content;
              onChunk(data.content);
            } else if (data.type === 'chunk') {
              fullResponse += data.content;
              onChunk(data.content);
            } else if (data.type === 'complete' || data.type === 'end') {
              eventSource.close();
              this.activeRequests.delete(requestId);
              resolve({
                message: fullResponse,
                metadata: data.metadata || { streaming: true },
              });
            } else if (data.type === 'error') {
              const errorContent = data.content || data.error || 'An error occurred';
              const nodeName = data.metadata?.nodeName || 'Unknown';
              throw new Error(`${nodeName}: ${errorContent}`);
            } else if (data.type === 'start') {
              console.log('🚀 Streaming started');
            }
          } catch (error) {
            console.error('❌ SSE parsing error:', error);
            eventSource.close();
            this.activeRequests.delete(requestId);
            reject(error);
          }
        };

        eventSource.onerror = (error) => {
          console.error('❌ SSE connection error:', error);
          eventSource.close();
          this.activeRequests.delete(requestId);
          reject(new Error('SSE connection failed'));
        };

        // Extended timeout for long-running AI requests (15 minutes)
        setTimeout(() => {
          if (this.activeRequests.has(requestId)) {
            eventSource.close();
            this.activeRequests.delete(requestId);
            reject(new Error('SSE连接15分钟后超时'));
          }
        }, N8N_WEBHOOK_CONFIG.streamTimeout);
      });
    } catch (error) {
      this.activeRequests.delete(requestId);
      throw this.handleError(error);
    }
  }

  /**
   * Stream response with files using POST to main endpoint
   */
  async streamWithFiles(payload, onChunk, requestId) {
    const formData = new FormData();
    formData.append('sessionId', this.getSessionId());
    formData.append('requestId', requestId);
    formData.append('action', 'sendMessage'); // Use sendMessage for streaming
    formData.append('stream', 'true'); // Request streaming response
    
    // Include the message text with files
    if (payload.message) {
      formData.append('message', payload.message);
    }
    
    // Include metadata if present
    if (payload.metadata) {
      formData.append('metadata', JSON.stringify(payload.metadata));
    }

    // Handle multiple files - match Flask backend format exactly
    if (payload.files) {
      const files = Array.isArray(payload.files) ? payload.files : [payload.files];
      console.log('📁 Streaming with files (Flask backend format):', files.map(f => ({ name: f.name, size: f.size, type: f.type })));

      // Flask backend expects 'file_{i}' format for multiple files
      files.forEach((file, index) => {
        formData.append(`file_${index}`, file, file.name);
      });
      
      // Add file count as Flask backend expects
      formData.append('fileCount', files.length.toString());
      
      // Add individual file metadata as Flask backend expects
      files.forEach((file, index) => {
        formData.append(`fileName_${index}`, file.name);
        formData.append(`fileType_${index}`, file.type);
        formData.append(`fileSize_${index}`, file.size.toString());
      });
    }

    // Backward compatibility for single file
    if (payload.file && !payload.files) {
      const file = payload.file;
      
      formData.append('file', file, file.name);
      formData.append('fileName', file.name);
      formData.append('fileType', file.type);
      formData.append('fileSize', file.size.toString());
    }

    const token = localStorage.getItem('access_token');
    const headers = {
      'X-Request-ID': requestId,
      'X-Session-ID': this.getSessionId(),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    console.log('📤 Streaming with files to Flask backend');

    // Create AbortController for timeout handling
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      console.warn('⏰ Stream request timeout after 15 minutes');
      abortController.abort();
    }, N8N_WEBHOOK_CONFIG.streamTimeout);

    // Track the controller for cleanup
    this.activeRequests.set(requestId, abortController);

    try {
      // Use fetch for streaming response with keepalive and timeout
      const response = await fetch(`${httpClient.defaults.baseURL}${N8N_WEBHOOK_CONFIG.baseUrl}`, {
        method: 'POST',
        body: formData,
        headers: headers,
        signal: abortController.signal,
        keepalive: false, // Disable keepalive for long-running streams
      });

      // Clear the timeout since we got a response
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`File upload failed: HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      // Check if response is streaming (text/event-stream, text/plain) or regular JSON
      const contentType = response.headers.get('content-type');

      if (contentType && (contentType.includes('text/event-stream') || contentType.includes('text/plain'))) {
        // Handle streaming response from Flask backend (raw HTTP streaming)
        console.log('📡 Receiving raw HTTP stream from Flask backend');
        return await this.handleRawStreamResponse(response, onChunk, requestId);
      } else {
        // Handle regular JSON response (new fire-and-forget pattern)
        console.log('📄 Receiving JSON response from Flask backend');
        const data = await response.json();

        // Check if this is a fire-and-forget streaming response
        if (data.streaming && data.streamUrl) {
          console.log('🚀 Fire-and-forget pattern: workflow triggered, establishing SSE connection');

          // Establish SSE connection to the provided stream URL
          const sseUrl = `${httpClient.defaults.baseURL}${data.streamUrl}`;
          console.log('🔗 Establishing SSE connection:', sseUrl);

          const eventSource = new EventSource(sseUrl);

          // Track active connection
          this.activeRequests.set(requestId, eventSource);

          return new Promise((resolve, reject) => {
            let fullResponse = '';

            eventSource.onopen = () => {
              console.log('✅ SSE connection established for fire-and-forget workflow');
            };

            eventSource.onmessage = (event) => {
              try {
                console.log('📨 SSE raw data:', event.data);
                const eventData = JSON.parse(event.data);
                console.log('📨 SSE parsed:', eventData);

                if (eventData.type === 'content') {
                  fullResponse += eventData.content;
                  onChunk(eventData.content);
                } else if (eventData.type === 'chunk') {
                  fullResponse += eventData.content;
                  onChunk(eventData.content);
                } else if (eventData.type === 'complete' || eventData.type === 'end') {
                  eventSource.close();
                  this.activeRequests.delete(requestId);
                  resolve({
                    message: fullResponse,
                    metadata: { ...data.metadata, streaming: true },
                  });
                } else if (eventData.type === 'error') {
                  const errorContent = eventData.content || eventData.error || 'An error occurred';
                  const nodeName = eventData.metadata?.nodeName || 'Unknown';
                  throw new Error(`${nodeName}: ${errorContent}`);
                } else if (eventData.type === 'start') {
                  console.log('🚀 Streaming started');
                }
              } catch (error) {
                console.error('❌ SSE parsing error:', error);
                eventSource.close();
                this.activeRequests.delete(requestId);
                reject(error);
              }
            };

            eventSource.onerror = (error) => {
              console.error('❌ SSE connection error:', error);
              eventSource.close();
              this.activeRequests.delete(requestId);
              reject(new Error('SSE连接失败'));
            };
          });
        } else if (data.message || data.output) {
          // Fallback: simulate streaming by sending the message content as chunks
          const content = data.message || data.output;
          onChunk(content);

          return {
            message: content,
            metadata: data.metadata || { streaming: false },
          };
        } else {
          throw new Error('响应中没有消息内容');
        }
      }
    } catch (error) {
      console.error('❌ Streaming with files failed:', error);
      // Clean up timeout and active requests
      clearTimeout(timeoutId);
      this.activeRequests.delete(requestId);

      // Handle abort error specifically
      if (error.name === 'AbortError') {
        throw new Error('流请求在15分钟后超时');
      }

      throw error;
    } finally {
      // Ensure cleanup happens even if error is thrown
      clearTimeout(timeoutId);
      this.activeRequests.delete(requestId);
    }
  }

  /**
   * Handle raw HTTP streaming response from Flask backend (text/plain)
   */
  async handleRawStreamResponse(response, onChunk, requestId) {
    return new Promise((resolve, reject) => {
      let fullResponse = '';
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');

      const readStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              console.log('📡 Raw HTTP stream completed');
              resolve({
                message: fullResponse,
                metadata: { streaming: true },
              });
              break;
            }

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.trim()) {
                try {
                  // Parse each line as JSON (n8n format: {"type":"item","content":"..."})
                  const data = JSON.parse(line);

                  // Handle n8n streaming format - extract content only, ignore metadata
                  if (data.type === 'item' && data.content) {
                    // n8n streaming format - only show content text, completely ignore metadata
                    fullResponse += data.content;
                    onChunk(data.content);  // Only send the content string, not the full object
                  } else if (data.type === 'content' || data.type === 'chunk') {
                    // Generic streaming format
                    fullResponse += data.content;
                    onChunk(data.content);
                  } else if (data.type === 'begin') {
                    console.log('🚀 Stream started (n8n begin marker)');
                  } else if (data.type === 'end') {
                    console.log('🏁 Stream completed (n8n end marker)');
                    resolve({
                      message: fullResponse,
                      metadata: data.metadata || { streaming: true },
                    });
                    return;
                  } else if (data.type === 'error') {
                    const errorContent = data.content || data.error || 'An error occurred';
                    reject(new Error(errorContent));
                    return;
                  } else if (data.metadata && !data.content) {
                    // Skip metadata-only responses (don't log or display)
                    console.log('⏭️ Skipping metadata-only response');
                  }
                } catch (parseError) {
                  // If line is not JSON, treat as raw text content
                  console.log('📨 Raw text chunk:', line);
                  fullResponse += line;
                  onChunk(line);
                }
              }
            }
          }
        } catch (error) {
          console.error('❌ Raw stream reading error:', error);
          reject(error);
        }
      };

      readStream();

      // Timeout handling
      setTimeout(() => {
        reader.cancel();
        reject(new Error('原始流在15分钟后超时'));
      }, N8N_WEBHOOK_CONFIG.streamTimeout);
    });
  }

  /**
   * Handle SSE response from Flask backend
   */
  async handleSSEResponse(response, onChunk, requestId) {
    return new Promise((resolve, reject) => {
      let fullResponse = '';
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      const readStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              console.log('📡 SSE stream completed');
              resolve({
                message: fullResponse,
                metadata: { streaming: true },
              });
              break;
            }
            
            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');
            
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const dataStr = line.substring(6);
                if (dataStr.trim()) {
                  try {
                    const data = JSON.parse(dataStr);
                    
                    if (data.type === 'content' || data.type === 'chunk') {
                      fullResponse += data.content;
                      onChunk(data.content);
                    } else if (data.type === 'complete' || data.type === 'end') {
                      console.log('🏁 Stream completed');
                      resolve({
                        message: fullResponse,
                        metadata: data.metadata || { streaming: true },
                      });
                      return;
                    } else if (data.type === 'error') {
                      const errorContent = data.content || data.error || 'An error occurred';
                      reject(new Error(errorContent));
                      return;
                    }
                  } catch (parseError) {
                    console.warn('⚠️ Failed to parse SSE data:', dataStr);
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error('❌ SSE reading error:', error);
          reject(error);
        }
      };
      
      readStream();
      
      // Timeout handling
      setTimeout(() => {
        reader.cancel();
        reject(new Error('SSE在15分钟后超时'));
      }, N8N_WEBHOOK_CONFIG.streamTimeout);
    });
  }

  /**
   * Send files via POST before establishing SSE connection
   */
  async sendFilesForStreaming(payload, requestId) {
    const formData = new FormData();
    formData.append('sessionId', this.getSessionId());
    formData.append('requestId', requestId);
    formData.append('action', 'uploadFiles');
    
    // Include the message text with files - match Flask backend expectations
    if (payload.message) {
      formData.append('message', payload.message);
    }
    
    // Include metadata if present
    if (payload.metadata) {
      formData.append('metadata', JSON.stringify(payload.metadata));
    }

    // Handle multiple files - match Flask backend format exactly
    if (payload.files) {
      const files = Array.isArray(payload.files) ? payload.files : [payload.files];
      console.log('📁 Pre-uploading files for streaming (Flask backend format):', files.map(f => ({ name: f.name, size: f.size, type: f.type })));

      // Flask backend expects 'file_{i}' format for multiple files
      files.forEach((file, index) => {
        formData.append(`file_${index}`, file, file.name);
      });
      
      // Add file count as Flask backend expects
      formData.append('fileCount', files.length.toString());
      
      // Add individual file metadata as Flask backend expects
      files.forEach((file, index) => {
        formData.append(`fileName_${index}`, file.name);
        formData.append(`fileType_${index}`, file.type);
        formData.append(`fileSize_${index}`, file.size.toString());
      });
      
      console.log('📁 Files formatted for Flask backend with file_X naming');
    }

    // Backward compatibility for single file - match Flask backend 'file' field
    if (payload.file && !payload.files) {
      const file = payload.file;
      
      formData.append('file', file, file.name);
      formData.append('fileName', file.name);
      formData.append('fileType', file.type);
      formData.append('fileSize', file.size.toString());
      
      console.log('📁 Single file formatted for Flask backend');
    }

    const token = localStorage.getItem('access_token');
    const headers = {
      'X-Request-ID': requestId,
      'X-Session-ID': this.getSessionId(),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    console.log('📤 Sending FormData to Flask backend with field names:', Array.from(formData.keys()));

    // Send files to Flask backend (which forwards to n8n)
    const response = await fetch(`${httpClient.defaults.baseURL}${N8N_WEBHOOK_CONFIG.baseUrl}`, {
      method: 'POST',
      body: formData,
      headers: headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ File upload failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText
      });
      throw new Error(`File upload failed: HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }

    const responseText = await response.text();
    console.log('✅ Files uploaded successfully to Flask backend, response:', responseText);
    return response;
  }



  /**
   * Cancel an active request
   */
  cancelRequest(requestId) {
    const controller = this.activeRequests.get(requestId);
    if (controller) {
      if (controller instanceof AbortController) {
        controller.abort();
      } else if (controller instanceof EventSource) {
        controller.close();
      }
      this.activeRequests.delete(requestId);
      return true;
    }
    return false;
  }

  /**
   * Cancel all active requests
   */
  cancelAllRequests() {
    this.activeRequests.forEach((controller, requestId) => {
      this.cancelRequest(requestId);
    });
  }

  /**
   * Process response from n8n
   */
  processResponse(response) {
    const data = response.data;

    console.log('📥 Processing n8n response:', {
      status: response.status,
      data: data,
      hasMessage: !!(data.message || data.response || data.output),
      messageLength: (data.message || data.response || data.output || '').length
    });

    // Handle different response formats
    if (data.error) {
      console.error('❌ n8n response contains error:', data.error);
      throw new Error(data.error);
    }

    let message = data.message || data.response || data.output || '';

    // Check if message contains error object (from n8n execution)
    if (message && typeof message === 'string') {
      try {
        // Try to parse message if it's a stringified object
        const parsed = JSON.parse(message);
        if (parsed.type === 'error') {
          // Extract user-friendly error message
          const errorContent = parsed.content || 'An error occurred';
          const nodeName = parsed.metadata?.nodeName || 'Unknown';
          throw new Error(`${nodeName}: ${errorContent}`);
        }
        // If it's a valid object but not an error, use the content
        if (parsed.content) {
          message = parsed.content;
        }
      } catch (parseError) {
        // Not JSON, keep original message
      }
    }

    // Check metadata for n8n error responses
    if (data.metadata?.n8nResponse?.type === 'error') {
      const errorContent = data.metadata.n8nResponse.content || 'An error occurred';
      const nodeName = data.metadata.nodeName || 'Unknown';
      throw new Error(`${nodeName}: ${errorContent}`);
    }

    if (!message) {
      console.warn('⚠️ No message content found in n8n response, using fallback');
    }

    const processedResponse = {
      message: message,
      metadata: {
        processingTime: data.processingTime,
        model: data.model,
        tokens: data.tokens,
        ...data.metadata,
      },
      success: true,
    };

    console.log('✅ Processed response:', {
      messageLength: processedResponse.message.length,
      hasMetadata: !!processedResponse.metadata,
      success: processedResponse.success
    });

    return processedResponse;
  }

  /**
   * Check if should retry request
   */
  shouldRetry(error, retryCount) {
    if (retryCount >= N8N_WEBHOOK_CONFIG.maxRetries) {
      return false;
    }

    // Retry on network errors or 5xx status codes
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
      return true;
    }

    if (error.response) {
      const status = error.response.status;
      return status >= 500 || status === 429;
    }

    return false;
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  calculateRetryDelay(retryCount) {
    const baseDelay = N8N_WEBHOOK_CONFIG.retryDelay;
    return Math.min(baseDelay * Math.pow(2, retryCount), 10000); // Max 10 seconds
  }

  /**
   * Handle and format errors
   */
  handleError(error) {
    if (error.name === 'AbortError') {
      return new Error('请求已取消');
    }

    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || error.message;

      switch (status) {
        case 429:
          this.setRateLimit(error.response.headers);
          return new Error('请求过多。请稍候再试。');
        case 503:
          return new Error('服务暂时不可用。请稍后再试。');
        case 500:
          return new Error('服务器错误。请再试一次。');
        case 400:
          return new Error(`无效请求: ${message}`);
        case 401:
          return new Error('需要身份验证。请重新登录。');
        default:
          return new Error(message || '发生了意外错误');
      }
    }

    if (error.code === 'ECONNABORTED') {
      return new Error('请求超时。请再试一次。');
    }

    return error;
  }

  /**
   * Rate limiting management
   */
  isRateLimited() {
    if (!this.rateLimitReset) return false;
    return new Date() < this.rateLimitReset;
  }

  setRateLimit(headers) {
    const resetTime = headers['x-ratelimit-reset'];
    if (resetTime) {
      this.rateLimitReset = new Date(parseInt(resetTime) * 1000);
    } else {
      // Default to 1 minute rate limit
      this.rateLimitReset = new Date(Date.now() + 60000);
    }
  }

  getRateLimitRemaining() {
    if (!this.rateLimitReset) return null;
    const remaining = Math.max(0, this.rateLimitReset - new Date());
    return Math.ceil(remaining / 1000); // Return seconds
  }

  /**
   * Session management
   */
  getSessionId() {
    let sessionId = localStorage.getItem('agentChatSessionId');
    if (!sessionId) {
      sessionId = this.generateSessionId();
      localStorage.setItem('agentChatSessionId', sessionId);
    }
    return sessionId;
  }

  /**
   * Create a new unique session ID (for new conversations)
   */
  createNewSession() {
    const sessionId = this.generateSessionId();
    localStorage.setItem('agentChatSessionId', sessionId);
    return sessionId;
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  generateRequestId() {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clear chat history for current session
   */
  async clearSession(sessionId = null) {
    const targetSessionId = sessionId || this.getSessionId();

    try {
      const response = await httpClient.delete(
        `${N8N_WEBHOOK_CONFIG.baseUrl}/history/${targetSessionId}/clear`,
        {
          timeout: 10000,
          headers: {
            'X-Session-ID': targetSessionId,
          }
        }
      );

      return {
        success: true,
        deletedCount: response.data.deleted_count || 0,
        sessionId: targetSessionId
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Start a new conversation (clear current + generate new session)
   */
  async newConversation() {
    const currentSessionId = this.getSessionId();

    try {
      // Clear current session history from database
      await this.clearSession(currentSessionId);
    } catch (error) {
      console.warn('Failed to clear old session:', error.message);
      // Continue anyway - we'll start fresh
    }

    // Generate new session ID using the dedicated method
    const newSessionId = this.createNewSession();

    // Clear any cached data
    this.clearLocalData();

    return {
      success: true,
      newSessionId,
      previousSessionId: currentSessionId
    };
  }

  /**
   * Clear local data/cache
   */
  clearLocalData() {
    // Clear any cached messages or session data
    localStorage.removeItem('chatHistory');
    localStorage.removeItem('chatMessages');

    // Cancel any active requests
    this.cancelAllRequests();

    // Reset rate limiting
    this.rateLimitReset = null;
  }

  /**
   * Set a specific session ID (useful for restoring sessions)
   */
  setSessionId(sessionId) {
    localStorage.setItem('agentChatSessionId', sessionId);
    return sessionId;
  }

  /**
   * Get chat history for a session
   */
  async getChatHistory(sessionId = null, options = {}) {
    const targetSessionId = sessionId || this.getSessionId();
    const {
      page = 1,
      pageSize = 50,
      messageType = null,
      search = ''
    } = options;

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });

      if (messageType) params.append('type', messageType);
      if (search) params.append('search', search);

      const response = await httpClient.get(
        `${N8N_WEBHOOK_CONFIG.baseUrl}/history/${targetSessionId}?${params}`,
        {
          timeout: 10000,
          headers: {
            'X-Session-ID': targetSessionId,
          }
        }
      );

      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get list of all chat sessions
   */
  async getChatSessions() {
    try {
      const response = await httpClient.get(
        `${N8N_WEBHOOK_CONFIG.baseUrl}/sessions`,
        {
          timeout: 10000
        }
      );

      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Search chat messages across sessions
   */
  async searchMessages(searchParams = {}) {
    const {
      query = '',
      sessionId = null,
      messageType = null,
      dateFrom = null,
      dateTo = null,
      hasFiles = null,
      page = 1,
      pageSize = 20
    } = searchParams;

    try {
      const payload = {
        query,
        page,
        page_size: pageSize
      };

      if (sessionId) payload.session_id = sessionId;
      if (messageType) payload.type = messageType;
      if (dateFrom) payload.date_from = dateFrom;
      if (dateTo) payload.date_to = dateTo;
      if (hasFiles !== null) payload.has_files = hasFiles;

      const response = await httpClient.post(
        `${N8N_WEBHOOK_CONFIG.baseUrl}/search`,
        payload,
        {
          timeout: 15000
        }
      );

      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Utility functions
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Health check for n8n webhook
   */
  async healthCheck() {
    try {
      const response = await httpClient.get(`${N8N_WEBHOOK_CONFIG.baseUrl}/health`, {
        timeout: 5000,
      });
      return response.data.status === 'healthy';
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }
}

// Create singleton instance
const agentChatService = new AgentChatService();

export default agentChatService;